import React, { useState, useRef, useEffect } from 'react';

interface Coordinates {
  x: number;
  y: number;
}

const DesignerPage = () => {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateSizes = () => {
      if (imgRef.current && containerRef.current) {
        const img = imgRef.current;
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;

        const imgRatio = imgWidth / imgHeight;
        const containerRatio = containerWidth / containerHeight;

        let newWidth;
        let newHeight;

        if (containerRatio > imgRatio) {
          newHeight = containerHeight;
          newWidth = newHeight * imgRatio;
        } else {
          newWidth = containerWidth;
          newHeight = newWidth / imgRatio;
        }

        setImageSize({ width: newWidth, height: newHeight });
        setContainerSize({ width: containerWidth, height: containerHeight });
      }
    };

    window.addEventListener('resize', updateSizes);
    if (imgRef.current) {
      imgRef.current.onload = updateSizes;
    }
    updateSizes();

    return () => window.removeEventListener('resize', updateSizes);
  }, []);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (imageContainerRef.current && imgRef.current) {
      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // 이미지 내부의 상대적 좌표 계산
      const relativeX = (x / imageSize.width) * imgRef.current.naturalWidth;
      const relativeY = (y / imageSize.height) * imgRef.current.naturalHeight;

      setCoordinates({
        x: Math.round(relativeX),
        y: Math.round(relativeY),
      });
    }
  };

  const imageContainerStyle = {
    width: `${imageSize.width}px`,
    height: `${imageSize.height}px`,
    position: 'absolute' as const,
    left: `${(containerSize.width - imageSize.width) / 2}px`,
    top: `${(containerSize.height - imageSize.height) / 2}px`,
    background: '#fff',
  };

  return (
    <div className="w-full h-full">
      <div
        ref={containerRef}
        style={{ background: '#fff' }}
        className="relative w-full h-[calc(100vh-200px)]"
      >
        <div
          ref={imageContainerRef}
          style={imageContainerStyle}
          onClick={handleImageClick}
          className="cursor-crosshair overflow-hidden"
        >
          <img
            ref={imgRef}
            src="/images/draw.png"
            alt="Clickable image"
            className="w-full h-full object-contain"
          />
          {coordinates && (
            <div
              className="absolute w-2 h-2 bg-red-500 rounded-full"
              style={{
                left: `${(coordinates.x / imgRef.current!.naturalWidth) * imageSize.width - 1}px`,
                top: `${(coordinates.y / imgRef.current!.naturalHeight) * imageSize.height - 1}px`,
              }}
            />
          )}
        </div>
      </div>
      <p className="mt-4">
        {coordinates
          ? `Clicked coordinates on original image: (x: ${coordinates.x}, y: ${coordinates.y})`
          : 'Click inside the image to see coordinates'}
      </p>
      <p>
        Displayed image size: {imageSize.width.toFixed(0)}x{imageSize.height.toFixed(0)}
      </p>
      <p>
        Original image size: {imgRef.current?.naturalWidth ?? 0}x
        {imgRef.current?.naturalHeight ?? 0}
      </p>
    </div>
  );
};

export default DesignerPage;
